import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Button,
    Chip,
    Divider,
    Paper,
    Stack,
    Typography,
} from "@mui/material";

const colors = {
    cream: "#fffaf2",
    paper: "rgba(255, 250, 242, 0.94)",
    white: "rgba(255,255,255,0.72)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    softMuted: "rgba(42, 38, 33, 0.48)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    border: "rgba(155,118,83,0.16)",
    success: "#4f7b58",
    successBg: "rgba(79,123,88,0.12)",
    blue: "#2f91b8",
    blueBg: "rgba(47,145,184,0.11)",
    warning: "#9a6a20",
    warningBg: "rgba(154,106,32,0.12)",
};

const managementCards = [
    {
        eyebrow: "Aktuelles",
        title: "Beiträge verwalten",
        text: "Erstellen, bearbeiten und veröffentlichen Sie Neuigkeiten für die Website.",
        icon: "✍️",
        buttonLabel: "Beiträge öffnen",
        to: "/admin/news",
        variant: "contained",
        tone: "brown",
    },
    {
        eyebrow: "Hinweis",
        title: "Website-Hinweis",
        text: "Aktivieren Sie kurze Meldungen für Besucher, zum Beispiel Betriebsferien, neue Ware oder Sonderöffnungszeiten.",
        icon: "📌",
        buttonLabel: "Hinweis bearbeiten",
        to: "/admin/notice",
        variant: "outlined",
        tone: "warning",
    },
    {
        eyebrow: "Kontakt",
        title: "Kontakt & Öffnungszeiten",
        text: "Pflegen Sie Adresse, Telefonnummer, E-Mail und die aktuellen Öffnungszeiten.",
        icon: "🕒",
        buttonLabel: "Kontakt bearbeiten",
        to: "/admin/contact",
        variant: "outlined",
        tone: "blue",
    },
];

const statusCards = [
    {
        label: "Admin-Zugang",
        value: "Geschützt",
        helper: "Nur eingeloggte Personen können Inhalte bearbeiten.",
    },
    {
        label: "Hinweisbanner",
        value: "Bereit",
        helper: "Kurze Meldungen können auf der Startseite angezeigt werden.",
    },
    {
        label: "Kontaktbereich",
        value: "CMS aktiv",
        helper: "Kontaktinformationen und Öffnungszeiten kommen aus Supabase.",
    },
    {
        label: "Newsbereich",
        value: "Bereit",
        helper: "Beiträge und Bilder können verwaltet werden.",
    },
];

const nextSteps = [
    "Startseiten-Texte direkt im CMS bearbeiten",
    "Bilder in einer Medienbibliothek zentral verwalten",
    "SEO-Felder für Beiträge und Seiten ergänzen",
];

function AdminDashboard() {
    const currentDate = new Intl.DateTimeFormat("de-DE", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    }).format(new Date());

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: {
                    xs: 3,
                    md: 4,
                },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    p: {
                        xs: 3,
                        sm: 4,
                        md: 5.5,
                    },
                    borderRadius: {
                        xs: 4,
                        md: 6,
                    },
                    border: `1px solid ${colors.border}`,
                    background:
                        "radial-gradient(circle at 88% 14%, rgba(155,118,83,0.18) 0%, rgba(155,118,83,0) 30%), radial-gradient(circle at 8% 92%, rgba(47,145,184,0.11) 0%, rgba(47,145,184,0) 34%), linear-gradient(145deg, rgba(255,250,242,0.98) 0%, rgba(247,241,232,0.93) 100%)",
                    boxShadow:
                        "0 28px 90px rgba(78,54,30,0.12), 0 2px 12px rgba(78,54,30,0.05)",
                }}
            >
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        width: 280,
                        height: 280,
                        borderRadius: "50%",
                        right: -100,
                        top: -120,
                        backgroundColor: "rgba(155,118,83,0.12)",
                        filter: "blur(8px)",
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "1.25fr 0.75fr",
                        },
                        gap: {
                            xs: 3,
                            md: 5,
                        },
                        alignItems: "center",
                    }}
                >
                    <Box>
                        <Typography
                            component="p"
                            sx={{
                                mb: 1,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 13,
                                fontWeight: 900,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.brown,
                            }}
                        >
                            Dashboard
                        </Typography>

                        <Typography
                            component="h1"
                            sx={{
                                mb: 1.6,
                                maxWidth: 820,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: {
                                    xs: "2.35rem",
                                    sm: "3.1rem",
                                    md: "4rem",
                                },
                                lineHeight: 0.95,
                                fontWeight: 800,
                                letterSpacing: "-0.065em",
                                color: colors.ink,
                            }}
                        >
                            Willkommen im CMS.
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 720,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: {
                                    xs: 16,
                                    md: 17,
                                },
                                lineHeight: 1.75,
                                color: colors.muted,
                            }}
                        >
                            Hier können Sie die wichtigsten Inhalte Ihrer Website
                            einfach und sicher selbst pflegen — ohne technische
                            Änderungen am Code.
                        </Typography>

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={1.4}
                            sx={{ mt: 3 }}
                        >
                            <Button
                                component={RouterLink}
                                to="/admin/notice"
                                variant="contained"
                                sx={primaryButtonStyles}
                            >
                                Hinweis bearbeiten
                            </Button>

                            <Button
                                component={RouterLink}
                                to="/"
                                variant="outlined"
                                sx={secondaryButtonStyles}
                            >
                                Website ansehen
                            </Button>
                        </Stack>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.6,
                            borderRadius: 4,
                            backgroundColor: "rgba(255,255,255,0.62)",
                            border: "1px solid rgba(155,118,83,0.14)",
                            backdropFilter: "blur(12px)",
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Chip
                                label="System bereit"
                                sx={{
                                    alignSelf: "flex-start",
                                    height: 34,
                                    px: 0.5,
                                    borderRadius: 999,
                                    backgroundColor: colors.successBg,
                                    color: colors.success,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontWeight: 900,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontSize: "1.55rem",
                                    lineHeight: 1.1,
                                    fontWeight: 800,
                                    letterSpacing: "-0.045em",
                                    color: colors.ink,
                                }}
                            >
                                Heute ist {currentDate}
                            </Typography>

                            <Typography
                                sx={{
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 14.5,
                                    lineHeight: 1.65,
                                    color: colors.muted,
                                }}
                            >
                                Tipp: Für kurzfristige Informationen wie Urlaub,
                                neue Ware oder Sonderöffnungszeiten nutzen Sie am
                                besten den Website-Hinweis.
                            </Typography>
                        </Stack>
                    </Paper>
                </Box>
            </Paper>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        lg: "repeat(4, 1fr)",
                    },
                    gap: 2,
                }}
            >
                {statusCards.map((item) => (
                    <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                            p: 2.6,
                            borderRadius: 4,
                            backgroundColor: colors.paper,
                            border: `1px solid ${colors.border}`,
                            boxShadow: "0 16px 44px rgba(78,54,30,0.06)",
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 0.8,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: colors.brown,
                            }}
                        >
                            {item.label}
                        </Typography>

                        <Typography
                            sx={{
                                mb: 0.7,
                                fontFamily: '"Quicksand", sans-serif',
                                fontSize: "1.85rem",
                                lineHeight: 1,
                                fontWeight: 800,
                                letterSpacing: "-0.05em",
                                color: colors.ink,
                            }}
                        >
                            {item.value}
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 14,
                                lineHeight: 1.55,
                                color: colors.softMuted,
                            }}
                        >
                            {item.helper}
                        </Typography>
                    </Paper>
                ))}
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(3, 1fr)",
                    },
                    gap: 3,
                }}
            >
                {managementCards.map((card) => (
                    <Paper
                        key={card.title}
                        elevation={0}
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            p: {
                                xs: 3,
                                md: 4,
                            },
                            borderRadius: 5,
                            backgroundColor: colors.paper,
                            border: `1px solid ${colors.border}`,
                            boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                        }}
                    >
                        <Box
                            aria-hidden="true"
                            sx={{
                                position: "absolute",
                                width: 120,
                                height: 120,
                                borderRadius: "50%",
                                right: -55,
                                top: -55,
                                backgroundColor: getCardGlow(card.tone),
                            }}
                        />

                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 2,
                                mb: 2.2,
                            }}
                        >
                            <Box>
                                <Typography
                                    component="p"
                                    sx={{
                                        mb: 1,
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 900,
                                        letterSpacing: "0.16em",
                                        textTransform: "uppercase",
                                        color: colors.brown,
                                    }}
                                >
                                    {card.eyebrow}
                                </Typography>

                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: '"Quicksand", sans-serif',
                                        fontSize: {
                                            xs: "2rem",
                                            md: "2.35rem",
                                        },
                                        lineHeight: 1,
                                        fontWeight: 800,
                                        letterSpacing: "-0.055em",
                                        color: colors.ink,
                                    }}
                                >
                                    {card.title}
                                </Typography>
                            </Box>

                            <Box
                                aria-hidden="true"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 58,
                                    height: 58,
                                    flexShrink: 0,
                                    borderRadius: 3,
                                    backgroundColor: getCardIconBg(card.tone),
                                }}
                            >
                                <Typography sx={{ fontSize: 25, lineHeight: 1 }}>
                                    {card.icon}
                                </Typography>
                            </Box>
                        </Box>

                        <Typography
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                mb: 3,
                                maxWidth: 620,
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 16,
                                lineHeight: 1.7,
                                color: colors.muted,
                            }}
                        >
                            {card.text}
                        </Typography>

                        <Button
                            component={RouterLink}
                            to={card.to}
                            variant={card.variant}
                            sx={
                                card.variant === "contained"
                                    ? primaryButtonStyles
                                    : secondaryButtonStyles
                            }
                        >
                            {card.buttonLabel}
                        </Button>
                    </Paper>
                ))}
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "0.95fr 1.05fr",
                    },
                    gap: 3,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: {
                            xs: 3,
                            md: 4,
                        },
                        borderRadius: 5,
                        backgroundColor: colors.paper,
                        border: `1px solid ${colors.border}`,
                        boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            mb: 1,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 13,
                            fontWeight: 900,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.brown,
                        }}
                    >
                        Schnellzugriff
                    </Typography>

                    <Typography
                        component="h2"
                        sx={{
                            mb: 2,
                            fontFamily: '"Quicksand", sans-serif',
                            fontSize: {
                                xs: "2rem",
                                md: "2.4rem",
                            },
                            lineHeight: 1,
                            fontWeight: 800,
                            letterSpacing: "-0.055em",
                            color: colors.ink,
                        }}
                    >
                        Häufige Aufgaben
                    </Typography>

                    <Stack
                        divider={
                            <Divider
                                sx={{
                                    borderColor: "rgba(42,38,33,0.1)",
                                }}
                            />
                        }
                        spacing={0}
                    >
                        <QuickAction
                            number="01"
                            title="Website-Hinweis ändern"
                            text="Für Urlaub, neue Ware, Sonderöffnungszeiten oder kurzfristige Meldungen."
                            to="/admin/notice"
                        />

                        <QuickAction
                            number="02"
                            title="Neue Neuigkeit veröffentlichen"
                            text="Ideal für Aktionen, Saisonartikel oder längere Informationen."
                            to="/admin/news/new"
                        />

                        <QuickAction
                            number="03"
                            title="Öffnungszeiten prüfen"
                            text="Kontaktinformationen und Zeiten aktuell halten."
                            to="/admin/contact"
                        />

                        <QuickAction
                            number="04"
                            title="Website kontrollieren"
                            text="Öffentliche Website nach Änderungen kurz ansehen."
                            to="/"
                        />
                    </Stack>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        p: {
                            xs: 3,
                            md: 4,
                        },
                        borderRadius: 5,
                        backgroundColor: colors.paper,
                        border: `1px solid ${colors.border}`,
                        boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            mb: 1,
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 13,
                            fontWeight: 900,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.brown,
                        }}
                    >
                        Ausbau
                    </Typography>

                    <Typography
                        component="h2"
                        sx={{
                            mb: 2,
                            fontFamily: '"Quicksand", sans-serif',
                            fontSize: {
                                xs: "2rem",
                                md: "2.4rem",
                            },
                            lineHeight: 1,
                            fontWeight: 800,
                            letterSpacing: "-0.055em",
                            color: colors.ink,
                        }}
                    >
                        Mögliche nächste CMS-Funktionen
                    </Typography>

                    <Stack
                        divider={
                            <Divider
                                sx={{
                                    borderColor: "rgba(42,38,33,0.1)",
                                }}
                            />
                        }
                        spacing={0}
                        sx={{ mb: 3 }}
                    >
                        {nextSteps.map((step, index) => (
                            <Box
                                key={step}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.6,
                                    py: 1.6,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 28,
                                        height: 28,
                                        flexShrink: 0,
                                        borderRadius: "50%",
                                        backgroundColor: "rgba(155,118,83,0.12)",
                                        color: colors.brownDark,
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 900,
                                    }}
                                >
                                    {index + 1}
                                </Box>

                                <Typography
                                    sx={{
                                        pt: 0.25,
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 15,
                                        lineHeight: 1.6,
                                        fontWeight: 800,
                                        color: colors.ink,
                                    }}
                                >
                                    {step}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <Typography
                        sx={{
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 14,
                            lineHeight: 1.7,
                            color: colors.muted,
                        }}
                    >
                        Das CMS ist jetzt so aufgebaut, dass weitere Bereiche sauber
                        ergänzt werden können, ohne die bestehende Website umzubauen.
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
}

function QuickAction({ number, title, text, to }) {
    return (
        <Box
            component={RouterLink}
            to={to}
            sx={{
                display: "grid",
                gridTemplateColumns: "44px 1fr",
                gap: 1.6,
                py: 1.8,
                textDecoration: "none",
                borderRadius: 3,

                "&:hover .quick-action-title": {
                    color: colors.brownDark,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    backgroundColor: "rgba(155,118,83,0.12)",
                    color: colors.brownDark,
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontSize: 12,
                    fontWeight: 900,
                }}
            >
                {number}
            </Box>

            <Box>
                <Typography
                    className="quick-action-title"
                    sx={{
                        mb: 0.4,
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 15.5,
                        lineHeight: 1.35,
                        fontWeight: 900,
                        color: colors.ink,
                        transition: "color 180ms ease",
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    sx={{
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: colors.softMuted,
                    }}
                >
                    {text}
                </Typography>
            </Box>
        </Box>
    );
}

function getCardIconBg(tone) {
    if (tone === "blue") return colors.blueBg;
    if (tone === "warning") return colors.warningBg;
    return "rgba(155,118,83,0.12)";
}

function getCardGlow(tone) {
    if (tone === "blue") return "rgba(47,145,184,0.1)";
    if (tone === "warning") return "rgba(154,106,32,0.1)";
    return "rgba(155,118,83,0.1)";
}

const primaryButtonStyles = {
    minHeight: 52,
    px: 3.2,
    borderRadius: 999,
    backgroundColor: colors.brownDark,
    color: "#fff",
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

    "&:hover": {
        backgroundColor: colors.brownDarker,
        boxShadow: "0 18px 38px rgba(111,81,54,0.28)",
    },
};

const secondaryButtonStyles = {
    minHeight: 52,
    px: 3.2,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: colors.brown,
    color: colors.brownDark,
    fontFamily: '"Nunito Sans", sans-serif',
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.07em",

    "&:hover": {
        borderWidth: 2,
        borderColor: colors.brownDark,
        backgroundColor: "rgba(155,118,83,0.08)",
    },
};

export default AdminDashboard;